import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  BarChart3,
  TrendingUp,
  Users,
  Calendar,
  Download,
  AlertTriangle,
  CheckCircle,
  Activity,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface StatsData {
  assessments: {
    total: number;
    completed: number;
    inProgress: number;
    byMonth: Array<{ month: string; count: number }>;
  };
  symptoms: {
    mostSelected: Array<{ name: string; count: number; risk?: string }>;
    byGroup: Array<{ group: string; count: number }>;
  };
  approaches: {
    topRecommended: Array<{ name: string; number?: number; count: number }>;
    effectiveness: Array<{ name: string; score: number }>;
  };
  users: {
    total: number;
    active: number;
    byRole: Array<{ role: string; count: number }>;
  };
  riskIndicators: {
    high: number;
    medium: number;
    low: number;
  };
}

interface Department {
  id: string;
  name: string;
}

export function Statistics() {
  const { profile } = useAuth();
  const { setHeaderAction, setPageTitle } = useAdminLayoutContext();
  const [dateRange, setDateRange] = useState('30days');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [stats, setStats] = useState<StatsData | undefined>(undefined);
  const [trends, setTrends] = useState<any[] | undefined>(undefined);
  const [approachDistribution, setApproachDistribution] = useState<any[] | undefined>(undefined);
  const [departments, setDepartments] = useState<Department[] | undefined>(undefined);

  const getDateFilter = useCallback(() => {
    const now = new Date();
    switch (dateRange) {
      case '7days': return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30days': return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      case '90days': return new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString();
      case '1year': return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000).toISOString();
      default: return null; // 'all'
    }
  }, [dateRange]);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!profile?.companyId) return;
      const { data } = await supabase
        .from('departments')
        .select('id, name')
        .eq('company_id', profile.companyId);
      setDepartments(data || []);
    };
    fetchDepartments();
  }, [profile?.companyId]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const dateFilter = getDateFilter();

        // Fetch assessments
        let assessmentQuery = supabase.from('assessment_stats').select('*');
        if (dateFilter) assessmentQuery = assessmentQuery.gte('created_at', dateFilter);
        if (selectedDepartment !== 'all') assessmentQuery = assessmentQuery.eq('department_id', selectedDepartment);
        const { data: assessments } = await assessmentQuery;

        const total = assessments?.length || 0;
        const completed = assessments?.filter(a => a.status === 'completed').length || 0;

        // Monthly breakdown
        const byMonth: Record<string, number> = {};
        assessments?.forEach(a => {
          const month = new Date(a.created_at).toLocaleDateString('nb-NO', { month: 'short', year: '2-digit' });
          byMonth[month] = (byMonth[month] || 0) + 1;
        });

        // Fetch users
        const { data: users } = await supabase.from('profiles').select('id, role, is_active');
        const activeUsers = users?.filter(u => u.is_active) || [];

        const byRole: Record<string, number> = {};
        users?.forEach(u => {
          byRole[u.role || 'user'] = (byRole[u.role || 'user'] || 0) + 1;
        });

        // Fetch approach distribution
        const { data: approachData } = await supabase
          .from('approaches')
          .select('id, name_no, number');

        // Risk indicators (simplified)
        const highRisk = assessments?.filter(a => a.risk_level === 'high').length || 0;
        const lowRisk = assessments?.filter(a => a.risk_level === 'low' || !a.risk_level).length || 0;

        setStats({
          assessments: {
            total,
            completed,
            inProgress: total - completed,
            byMonth: Object.entries(byMonth).map(([month, count]) => ({ month, count })),
          },
          symptoms: {
            mostSelected: [],
            byGroup: [],
          },
          approaches: {
            topRecommended: (approachData || []).map(a => ({ name: a.name_no, number: a.number, count: 0 })),
            effectiveness: [],
          },
          users: {
            total: users?.length || 0,
            active: activeUsers.length,
            byRole: Object.entries(byRole).map(([role, count]) => ({ role, count })),
          },
          riskIndicators: {
            high: highRisk,
            medium: 0,
            low: lowRisk,
          },
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchStats();
  }, [dateRange, selectedDepartment, getDateFilter]);

  // Fetch trends
  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const days = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 90;
        const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase
          .from('assessment_stats')
          .select('created_at')
          .gte('created_at', since)
          .order('created_at');

        // Group by date
        const byDate: Record<string, number> = {};
        data?.forEach(a => {
          const date = new Date(a.created_at).toISOString().split('T')[0];
          byDate[date] = (byDate[date] || 0) + 1;
        });

        setTrends(Object.entries(byDate).map(([date, count]) => ({ date, count })));
      } catch (error) {
        console.error('Error fetching trends:', error);
      }
    };

    fetchTrends();
  }, [dateRange]);

  // Fetch approach distribution
  useEffect(() => {
    const fetchDistribution = async () => {
      try {
        const { data } = await supabase
          .from('approaches')
          .select('name_no, number');

        setApproachDistribution(
          (data || []).map(a => ({ name: `M${a.number} ${a.name_no}`, value: 1 }))
        );
      } catch (error) {
        console.error('Error fetching approach distribution:', error);
      }
    };

    fetchDistribution();
  }, [dateRange]);


  // Set page title
  useEffect(() => {
    setPageTitle('Statistikk');
    return () => setPageTitle(null);
  }, [setPageTitle]);

  const handleExport = () => {
    if (!stats) return;

    const csvContent = [
      ['Helsenøkkelen Statistics Export'],
      ['Generated:', new Date().toLocaleString('nb-NO')],
      ['Period:', dateRange],
      [''],
      ['Vurderinger'],
      ['Totalt:', stats.assessments.total],
      ['Fullført:', stats.assessments.completed],
      [''],
      ['Mest anbefalte tilnærminger'],
      ...stats.approaches.topRecommended.map(a => [a.name, a.count]),
      [''],
      ['Brukere'],
      ['Totalt:', stats.users.total],
      ['Aktive:', stats.users.active],
      [''],
      ['Risikovurderinger'],
      ['Kritisk risiko:', stats.riskIndicators.high],
      ['Normal:', stats.riskIndicators.low],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `helsenokkelen-statistikk-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Set header action
  useEffect(() => {
    setHeaderAction(
      <button
        onClick={handleExport}
        className="flex items-center gap-2 px-4 py-2 bg-green-600 dark:bg-green-500 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors"
        disabled={!stats}
      >
        <Download className="h-5 w-5" />
        Eksporter
      </button>
    );
    return () => setHeaderAction(null);
  }, [setHeaderAction, stats]);

  if (stats === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Laster statistikk...</span>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#F97316'];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-gray-400" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            >
              <option value="7days">Siste 7 dager</option>
              <option value="30days">Siste 30 dager</option>
              <option value="90days">Siste 90 dager</option>
              <option value="1year">Siste år</option>
              <option value="all">Alle</option>
            </select>
          </div>

          {departments && departments.length > 0 && (
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-400" />
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Alle avdelinger</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <BarChart3 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Totalt vurderinger</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.assessments.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Aktive brukere</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.users.active}</p>
              <p className="text-xs text-gray-400">av {stats.users.total} totalt</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Kritisk risiko</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.riskIndicators.high}</p>
              <p className="text-xs text-gray-400">vurderinger med modul 8/9</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Activity className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Normal risiko</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.riskIndicators.low}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-600" />
            Vurderinger over tid
          </h3>
          {trends && trends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getDate()}/${date.getMonth() + 1}`;
                  }}
                />
                <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString('nb-NO')}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', r: 4 }}
                  name="Vurderinger"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <TrendingUp className="h-12 w-12 mb-3 opacity-50" />
              <p>Ingen data tilgjengelig</p>
              <p className="text-sm mt-1">Fullfør vurderinger for å se trender</p>
            </div>
          )}
        </div>

        {/* Approach Distribution Pie Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Fordeling av tilnærminger
          </h3>
          {approachDistribution && approachDistribution.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={approachDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${(name || '').split(' ')[0]} ${((percent || 0) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {approachDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <CheckCircle className="h-12 w-12 mb-3 opacity-50" />
              <p>Ingen data tilgjengelig</p>
              <p className="text-sm mt-1">Fullfør vurderinger for å se fordeling</p>
            </div>
          )}
        </div>
      </div>

      {/* Bar Chart - Top Approaches */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-purple-600" />
          Mest anbefalte tilnærminger
        </h3>
        {stats.approaches.topRecommended.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={stats.approaches.topRecommended} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
              <XAxis type="number" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
              <YAxis
                type="category"
                dataKey="name"
                stroke="#9CA3AF"
                tick={{ fontSize: 12 }}
                width={150}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Bar dataKey="count" fill="#8B5CF6" radius={[0, 4, 4, 0]} name="Antall" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <BarChart3 className="h-12 w-12 mb-3 opacity-50" />
            <p>Ingen data tilgjengelig</p>
            <p className="text-sm mt-1">Fullfør vurderinger for å se anbefalinger</p>
          </div>
        )}
      </div>

      {/* Monthly Trend */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-orange-600" />
          Månedlig oversikt
        </h3>
        {stats.assessments.byMonth.length > 0 && stats.assessments.byMonth.some(m => m.count > 0) ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.assessments.byMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
              <XAxis dataKey="month" stroke="#9CA3AF" tick={{ fontSize: 12 }} />
              <YAxis stroke="#9CA3AF" tick={{ fontSize: 12 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Vurderinger" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Calendar className="h-12 w-12 mb-3 opacity-50" />
            <p>Ingen data tilgjengelig</p>
            <p className="text-sm mt-1">Fullfør vurderinger for å se månedlig oversikt</p>
          </div>
        )}
      </div>

      {/* Users by Role */}
      {stats.users.byRole.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-cyan-600" />
            Brukere per rolle
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {stats.users.byRole.map((item) => (
              <div
                key={item.role}
                className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
              >
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{item.count}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{item.role}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
